import { useState } from 'react'
import {
  Check,
  ClipboardCheck,
  Copy,
  ExternalLink,
  Eye,
  Hand,
  MonitorCog,
  ShieldAlert,
  Stethoscope,
} from 'lucide-react'

type ExplorerTab = 'works' | 'benefits' | 'limits' | 'questions'

const tabs: { id: ExplorerTab; label: string }[] = [
  { id: 'works', label: 'How it works' },
  { id: 'benefits', label: 'Why it matters' },
  { id: 'limits', label: 'Limits & safety' },
  { id: 'questions', label: 'Questions to ask' },
]

const questions = [
  'Why do you recommend robotic assistance for my specific condition and anatomy?',
  'What are the open-surgery and conventional keyhole-surgery alternatives?',
  'What benefits are supported for this exact procedure—not robotic surgery in general?',
  'How many of these procedures have you and your hospital team performed?',
  'What is the chance of converting to open surgery, and what is the backup plan?',
  'Which procedure-specific risks could affect function, pain, fertility or recovery?',
  'If this is cancer surgery, what is known about margins, recurrence and survival?',
  'What should I expect for operating time, hospital stay, recovery and cost?',
]

const evidenceOutcomes = [
  { label: 'Total complications', robotic: 4, noDifference: 31, laparoscopy: 0 },
  { label: 'Operative time', robotic: 4, noDifference: 11, laparoscopy: 16 },
  { label: 'Estimated blood loss', robotic: 3, noDifference: 12, laparoscopy: 3 },
  { label: 'Conversion rate', robotic: 3, noDifference: 15, laparoscopy: 0 },
  { label: 'Postoperative stay', robotic: 5, noDifference: 27, laparoscopy: 0 },
  { label: 'Total cost', robotic: 0, noDifference: 2, laparoscopy: 11 },
]

export function RoboticSurgeryExplorer() {
  const [activeTab, setActiveTab] = useState<ExplorerTab>('works')
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([0, 1, 2, 3])
  const [copyStatus, setCopyStatus] = useState('')

  const toggleQuestion = (index: number) => {
    setSelectedQuestions((current) => current.includes(index)
      ? current.filter((item) => item !== index)
      : [...current, index])
    setCopyStatus('')
  }

  const copyQuestions = async () => {
    if (!selectedQuestions.length) {
      setCopyStatus('Choose at least one question first.')
      return
    }

    const text = [
      'Questions for my surgeon about robotic-assisted surgery',
      '',
      ...[...selectedQuestions].sort((a, b) => a - b).map((index, position) => `${position + 1}. ${questions[index]}`),
      '',
      'Prepared on fathimafinu.com. This list is educational and does not recommend a surgical approach.',
    ].join('\n')

    try {
      await navigator.clipboard.writeText(text)
      setCopyStatus(`${selectedQuestions.length} question${selectedQuestions.length === 1 ? '' : 's'} copied.`)
    } catch {
      setCopyStatus('Copy was blocked by this browser. You can still read or print the questions here.')
    }
  }

  return (
    <section className="section robotics-section" id="robotic-surgery">
      <div className="container">
        <div className="robotics-heading">
          <div>
            <span className="eyebrow"><Stethoscope size={15} /> Surgical technology, explained</span>
            <h2>Robotic-assisted surgery: <span className="serif-accent">advanced tools, human decisions.</span></h2>
          </div>
          <p>Systems such as <a href="https://www.intuitive.com/en-us/products-and-services/da-vinci" target="_blank" rel="noreferrer">Intuitive’s da Vinci platform <ExternalLink size={13} /></a> can help trained surgeons perform selected minimally invasive procedures. The surgeon—not the robot—controls every movement.</p>
        </div>

        <div className="robotics-feature">
          <figure className="robotics-visual">
            <img
              src="/images/robotic-surgery-concept.png"
              width="1792"
              height="1024"
              loading="lazy"
              decoding="async"
              alt="Conceptual operating-room scene with a surgeon controlling articulated surgical instruments from a console"
            />
            <figcaption>AI-generated educational concept · not a device or procedure diagram</figcaption>
          </figure>

          <div className="robotics-explorer">
            <div className="robotics-human-control"><Hand size={20} /><span><strong>Direct human control</strong>The system assists; it does not independently decide or operate.</span></div>

            <div className="robotics-tabs" role="tablist" aria-label="Explore robotic-assisted surgery">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`robotics-panel-${tab.id}`}
                  id={`robotics-tab-${tab.id}`}
                  className={activeTab === tab.id ? 'is-active' : ''}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="robotics-panel" role="tabpanel" id={`robotics-panel-${activeTab}`} aria-labelledby={`robotics-tab-${activeTab}`}>
              {activeTab === 'works' && (
                <div className="robotics-flow">
                  <article><MonitorCog size={24} /><span>01</span><h3>Surgeon console</h3><p>The surgeon views the operative field and translates hand movements into instrument movements in real time.</p></article>
                  <article><Eye size={24} /><span>02</span><h3>Vision system</h3><p>A magnified three-dimensional view can make detailed anatomy easier to see during selected procedures.</p></article>
                  <article><Hand size={24} /><span>03</span><h3>Patient-side instruments</h3><p>Small, wristed instruments can move precisely in confined areas while the bedside team remains present.</p></article>
                </div>
              )}

              {activeTab === 'benefits' && (
                <div className="robotics-copy-panel">
                  <h3>Where the technology may help</h3>
                  <p>For an appropriate patient and operation, robotic assistance may support a minimally invasive approach, detailed vision, fine instrument control and access to confined anatomy.</p>
                  <ul>
                    <li><Check size={17} /> Smaller incisions may be possible compared with open surgery.</li>
                    <li><Check size={17} /> Some patients may have less blood loss or pain and a shorter recovery.</li>
                    <li><Check size={17} /> Benefits vary by procedure and are not unique to every robotic approach.</li>
                  </ul>
                  <div className="robotics-evidence-note">Compared with conventional keyhole surgery, randomized trials often find similar major outcomes. The right comparison is always procedure-specific.</div>
                </div>
              )}

              {activeTab === 'limits' && (
                <div className="robotics-copy-panel robotics-limits-panel">
                  <h3>Technology does not remove surgical risk</h3>
                  <p>Bleeding, infection, clots, injury to nearby tissues, anesthesia complications and—rarely—death remain possible. Surgery may take longer, equipment can malfunction, and conversion to open surgery may be necessary.</p>
                  <div className="robotics-warning"><ShieldAlert size={21} /><span><strong>For cancer surgery</strong>FDA device clearance is not proof of improved cancer survival or recurrence outcomes. Ask for evidence about your exact diagnosis and operation.</span></div>
                  <p>Patient factors, the disease, the specific system and the surgeon’s experience all affect suitability and outcomes.</p>
                </div>
              )}

              {activeTab === 'questions' && (
                <div className="robotics-question-builder">
                  <div><h3>Build a private question list</h3><p>Select what matters. Your choices stay in this browser and are not submitted.</p></div>
                  <div className="robotics-question-list">
                    {questions.map((question, index) => (
                      <label key={question}>
                        <input type="checkbox" checked={selectedQuestions.includes(index)} onChange={() => toggleQuestion(index)} />
                        <span><i><Check size={13} /></i>{question}</span>
                      </label>
                    ))}
                  </div>
                  <div className="robotics-copy-row">
                    <button type="button" className="button button-small" onClick={copyQuestions}><Copy size={16} /> Copy selected questions</button>
                    <span aria-live="polite">{copyStatus}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="robotics-results">
          <div className="robotics-results-heading">
            <div><span className="eyebrow">What randomized studies found</span><h3>Most comparisons found <span className="serif-accent">no significant difference.</span></h3></div>
            <p>A 2023 systematic review qualitatively compared robotic and conventional laparoscopic surgery across multiple abdominal and pelvic procedures. It did not pool patient-level effects.</p>
          </div>
          <div className="robotics-results-layout">
            <div className="robotics-study-numbers" aria-label="Systematic review size">
              <span><strong>45</strong><small>included studies</small></span>
              <span><strong>7,364</strong><small>patients</small></span>
              <span><strong>13</strong><small>procedures</small></span>
            </div>
            <div className="robotics-outcome-plot">
              <div className="robotics-plot-legend" aria-hidden="true"><span className="is-robotic">Robotic favoured</span><span className="is-neutral">No significant difference</span><span className="is-laparoscopy">Laparoscopy favoured</span></div>
              {evidenceOutcomes.map((outcome) => {
                const total = outcome.robotic + outcome.noDifference + outcome.laparoscopy
                return (
                  <div className="robotics-outcome-row" key={outcome.label}>
                    <span>{outcome.label}</span>
                    <div className="robotics-outcome-bar" role="img" aria-label={`${outcome.label}: ${outcome.robotic} studies favoured robotic surgery, ${outcome.noDifference} found no significant difference, and ${outcome.laparoscopy} favoured laparoscopy; ${total} reporting studies`}>
                      {outcome.robotic > 0 && <i className="is-robotic" style={{ width: `${(outcome.robotic / total) * 100}%` }}><b>{outcome.robotic}</b></i>}
                      {outcome.noDifference > 0 && <i className="is-neutral" style={{ width: `${(outcome.noDifference / total) * 100}%` }}><b>{outcome.noDifference}</b></i>}
                      {outcome.laparoscopy > 0 && <i className="is-laparoscopy" style={{ width: `${(outcome.laparoscopy / total) * 100}%` }}><b>{outcome.laparoscopy}</b></i>}
                    </div>
                    <small>{total} studies</small>
                  </div>
                )
              })}
            </div>
          </div>
          <p className="robotics-results-note">Counts show the direction reported by studies for each outcome—not pooled patient outcome rates. “Favoured” means a statistically significant result in that study; it does not establish that one approach is best for every operation or patient. No formal meta-analysis was conducted.</p>
        </div>

        <div className="robotics-sources">
          <span><ClipboardCheck size={18} /> Reviewed 3 October 2026</span>
          <a href="https://www.fda.gov/medical-devices/surgery-devices/computer-assisted-surgical-systems" target="_blank" rel="noreferrer">FDA patient guidance <ExternalLink size={13} /></a>
          <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10462573/" target="_blank" rel="noreferrer">Full randomized-study review <ExternalLink size={13} /></a>
          <a href="https://www.intuitive.com/en-us/about-us/company/legal/safety-information" target="_blank" rel="noreferrer">Intuitive safety information <ExternalLink size={13} /></a>
          <small>da Vinci is a trademark of Intuitive Surgical. This independent educational site is not affiliated with or endorsed by Intuitive.</small>
        </div>
      </div>
    </section>
  )
}
